import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ReactGA from "react-ga4";
import { initMetaPixel, trackMetaPageView } from "./services/metaPixel";
import { VehicleProvider } from "./context/VehicleContext";
import { LanguageProvider } from "./context/LanguageContext";
import LanguageRouter from "./components/shared/LanguageRouter";
import {
  Landing,
  SurfPage,
  VanCatalog,
  CamperDetails,
  WaveCatchersDetails,
  FamilyCatchersDetails,
  SaleDetails,
  UnderConstruction,
  CheckoutStep1,
  CheckoutStep2,
  CheckoutStep3,
  CheckoutSuccess,
  About,
  Blog,
  BlogPost,
  Contact,
  FAQ,
  PrivacyPolicy,
  CookiePolicy,
  TermsAndConditions,
} from "./views";
import { ScrollToTop, CookieBanner } from "./components/shared";
import { CookieConsentProvider, useCookieConsent } from "./context/CookieConsentContext";
import './i18n/config'; // Initialize i18n
import "./styles/landing/Landing.css";
import "./styles/landing/VideoBackground.css";
import "./styles/camperDetails/CamperDetails.css";
import "./styles/camperDetails/WaveCatchersDetails.css";
import "./styles/camperDetails/FamilyCatchersDetails.css";
import "./styles/sale/Sale.css";
import "./styles/saleDetails/SaleDetails.css";
import "./styles/about/About.css";
import "./styles/blog/Blog.css";
import "./styles/blogPost/BlogPost.css";
import "./styles/contact/Contact.css";
import "./styles/faq/FAQ.css";
import "./styles/shared/CookieBanner.css";
import "./styles/legal/LegalPages.css";
import "./styles/shared/Header.css";

function PageTracker() {
  const location = useLocation();
  const { consentGiven, preferences } = useCookieConsent();
  const [gaInitialized, setGaInitialized] = useState(false);
  const [pixelInitialized, setPixelInitialized] = useState(false);

  // Initialize GA once when consent is given
  useEffect(() => {
    if (consentGiven && preferences.analytics && !gaInitialized) {
      const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
      if (measurementId) {
        ReactGA.initialize(measurementId);
        setGaInitialized(true);
        console.log('Google Analytics initialized:', measurementId);
      }
    }
  }, [consentGiven, preferences.analytics, gaInitialized]);

  // Track page views on route changes (only if GA is initialized)
  useEffect(() => {
    if (gaInitialized) {
      ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
      console.log('GA Pageview tracked:', location.pathname + location.search);
    }
  }, [location, gaInitialized]);

  // The Meta pixel is an advertising tracker, so it waits on marketing consent
  // rather than the analytics toggle GA uses.
  useEffect(() => {
    if (consentGiven && preferences.marketing && !pixelInitialized) {
      const pixelId = process.env.REACT_APP_META_PIXEL_ID;
      if (pixelId) {
        initMetaPixel(pixelId);
        setPixelInitialized(true);
      }
    }
  }, [consentGiven, preferences.marketing, pixelInitialized]);

  // Single-page app, so each route change is its own PageView. Consent is
  // re-checked rather than relying on pixelInitialized: fbevents.js cannot be
  // unloaded once fetched, so a visitor who grants marketing consent and then
  // withdraws it would otherwise keep being tracked for the rest of the
  // session.
  useEffect(() => {
    if (pixelInitialized && preferences.marketing) {
      trackMetaPageView();
    }
  }, [location, pixelInitialized, preferences.marketing]);

  return null;
}

function App() {
  return (
    <HelmetProvider>
      <CookieConsentProvider>
        <Router>
          <LanguageProvider>
          <VehicleProvider>
            <PageTracker />
            <ScrollToTop />
            <div className="App">
              <Routes>
                {/* Redirect root to default language */}
                <Route path="/" element={<Navigate to="/en" replace />} />

                {/* Language-prefixed routes */}
                <Route path="/:lang" element={<LanguageRouter />}>
                  <Route index element={<Landing />} />
                  <Route path="surf" element={<SurfPage />} />
                  <Route path="catalog" element={<VanCatalog />} />
                  <Route path="campervan/:id" element={<CamperDetails />} />
                  <Route path="campervan/sun-catchers" element={<CamperDetails />} />
                  <Route path="campervan/wave-catchers" element={<WaveCatchersDetails />} />
                  <Route path="campervan/family-catchers" element={<FamilyCatchersDetails />} />
                  <Route path="sale" element={<UnderConstruction />} />
                  <Route path="sale/:id" element={<SaleDetails />} />
                  <Route path="about" element={<About />} />
                  <Route path="blog" element={<Blog />} />
                  <Route path="blog/:id" element={<BlogPost />} />
                  <Route path="contact" element={<Contact />} />
                  <Route path="faq" element={<FAQ />} />
                  <Route path="privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="cookie-policy" element={<CookiePolicy />} />
                  <Route path="terms-and-conditions" element={<TermsAndConditions />} />
                  {/* Reordered flow: add-ons first, then details, then insurance + pay.
                      Routes keep the step1/2/3 URLs; the rendered view is swapped. */}
                  <Route path="checkout/step1" element={<CheckoutStep3 />} /> {/* Add-ons */}
                  <Route path="checkout/step2" element={<CheckoutStep1 />} /> {/* Your details */}
                  <Route path="checkout/step3" element={<CheckoutStep2 />} /> {/* Insurance + pay */}
                  <Route path="checkout/success" element={<CheckoutSuccess />} />
                </Route>
              </Routes>
            </div>
            <CookieBanner />
          </VehicleProvider>
        </LanguageProvider>
        </Router>
      </CookieConsentProvider>
    </HelmetProvider>
  );
}

export default App;


// // Import the functions you need from the SDKs you need
// import { initializeApp } from "firebase/app";
// import { getAnalytics } from "firebase/analytics";
// // TODO: Add SDKs for Firebase products that you want to use
// // https://firebase.google.com/docs/web/setup#available-libraries

// // Your web app's Firebase configuration
// // For Firebase JS SDK v7.20.0 and later, measurementId is optional
// const firebaseConfig = {
//   apiKey: "AIzaSyAR-XQPikki0j4vaRClVPUicZ-x4ileftg",
//   authDomain: "suncatchers-portugal.firebaseapp.com",
//   projectId: "suncatchers-portugal",
//   storageBucket: "suncatchers-portugal.firebasestorage.app",
//   messagingSenderId: "12816504561",
//   appId: "1:12816504561:web:58ec19c50ace9cdc63bb50",
//   measurementId: "G-XDL8R9F9FG"
// };

// // Initialize Firebase
// const app = initializeApp(firebaseConfig);
// const analytics = getAnalytics(app);